import React, { useState, useEffect, useMemo } from 'react'
import { Dialog } from 'primereact/dialog'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import Header from './components/Header.tsx'
import Main from './components/Main.tsx'
import Footer from './components/Footer.tsx'
import Product from './components/Product.tsx'
import Cart from './components/Cart.tsx'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import ExitSimulation from '@aytm/app/swidget/base/questions/_common/ExitSimulation.tsx'
import { IEcommerceQuestionViewProps } from '../types.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useLoadScriptInFrame from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useLoadScriptInFrame.ts'
import useScrollMemory from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useScrollMemory.ts'
import style from './Nike.module.scss'
import { formatPriceWithLocale } from '../utils/formatPriceWithLocale.ts'
import useSurveyLocale from '../../../modules/locale/useSurveyLocale.ts'

const NikeView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const session = useSession()
  const na = useMemo(() => schema.answers.find(answer => answer.na), [schema])
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + cur.v * cur.price, 0)
  ]
  const selectedIndex = response.answers.findIndex(r => r.selected)
  const timeline = useTimeline()
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const theme = useTheme()
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')
  const [showCart, setShowCart] = useState<boolean>(false)
  const viewport = useViewport()
  const [needHint, setNeedHint] = useState(true)
  const [containerTop, setContainerTop] = useState<number>(0)
  const { containerRef, registerCardRef, findCenterElement, restorePosition, scrollTop } = useScrollMemory()
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)
  const { t, languageTag } = useSurveyLocale()
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)

  const renderError = () => {
    if (!valid) {
      return (
        <div
          className={style.error}
          dangerouslySetInnerHTML={{
            __html:
              (schema.max_money_spent && totals[1] > schema.max_money_spent
                ? t<(max: string) => string>(
                    'questions',
                    'ec',
                    'maxSpentError'
                  )(formatPriceWithLocale(schema.max_money_spent, schema.price_mask, languageTag))
                : '') +
              (schema.max_cart_size && totals[0] > schema.max_cart_size
                ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
                : '')
          }}
        />
      )
    }
    return null
  }

  // Desktop tracks the scroll offset (for the sticky filter bar) on top of the shared center-memory.
  const onContainerScroll = () => {
    if (!viewport.mobile && containerRef.current) {
      requestAnimationFrame(() => {
        setContainerTop(containerRef.current.scrollTop)
      })
    }
    findCenterElement()
  }

  useEffect(() => {
    if (!showCart && selectedIndex < 0) {
      restorePosition()
    }
  }, [showCart, selectedIndex, viewport.orientation, restorePosition])

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
    }
    onUpdateResponse({
      answers: response.answers.map(r => {
        return { ...r, selected: false }
      }),
      clicked: response.clicked
    })
  }

  const onItemSelect = (index: number) => {
    scrollTop()
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    response.answers[index].selected = true
    timeline.log({
      target: '0,0',
      type: 'open_popup',
      ref: response.answers[index].id
    })
    onUpdateResponse(response)
  }

  const onItemClose = () => {
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
      response.answers[selectedIndex].selected = false
      onUpdateResponse(response)
    }
  }

  const onCartOpen = () => {
    scrollTop()
    if (selectedIndex >= 0) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
      response.answers[selectedIndex].selected = false
      onUpdateResponse(response)
    }
    setShowCart(true)
    timeline.log({
      type: 'open_cart'
    })
  }

  const onCartClose = () => {
    setShowCart(false)
    timeline.log({
      type: 'close_cart'
    })
  }

  const onAddToCart = (qnt: number, index: number) => {
    response.answers[index].v += qnt
    if (selectedIndex < 0 && !showCart) {
      timeline.log({
        target: '0,0',
        type: 'open_popup',
        ref: response.answers[index].id
      })
    }
    timeline.log({
      type: showCart ? 'edit_in_cart' : 'add_to_cart',
      ref: response.answers[index].id,
      target: selectedIndex >= 0 || showCart ? response.answers[index].v.toString() : '1'
    })
    if ((!showCart && selectedIndex < 0) || (findability && selectedIndex >= 0)) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[index].id
      })
    }
    onUpdateResponse(response)
  }

  const onAddToCartFromCounter = (qnt: number, index: number) => {
    response.answers[index].v = qnt
    timeline.log({
      type: 'edit_in_cart',
      ref: response.answers[index].id,
      target: response.answers[index].v.toString()
    })
    onUpdateResponse(response)
  }

  const onHomeClick = () => {
    showCart ? onCartClose() : onItemClose()
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div
        className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`}
        ref={containerRef}
        onScroll={onContainerScroll}
      >
        <Header
          containerTop={containerTop}
          schema={schema}
          findability={findability}
          totals={totals}
          onMainPage={showCart || selectedIndex >= 0}
          onCartPage={showCart}
          onCartOpen={onCartOpen}
          onHomeClick={onHomeClick}
          clearSelection={clearSelection}
        />
        {selectedIndex >= 0 ? (
          <Product
            answer={schema.answers[selectedIndex]}
            schema={schema}
            quantity={response.answers[selectedIndex].v}
            index={selectedIndex}
            totals={totals}
            findability={findability}
            onAddToCart={onAddToCart}
            onCartOpen={onCartOpen}
            scrollTop={scrollTop}
            checkout={checkout}
          />
        ) : showCart ? (
          <Cart
            response={response}
            schema={schema}
            totals={totals}
            findability={findability}
            renderError={renderError}
            onAddToCart={onAddToCart}
            onAddToCartFromCounter={onAddToCartFromCounter}
            onItemSelect={onItemSelect}
            onUpdateResponse={onUpdateResponse}
            checkout={checkout}
            scrollTop={scrollTop}
          />
        ) : (
          <Main
            containerTop={containerTop}
            schema={schema}
            onItemSelect={onItemSelect}
            registerCardRef={registerCardRef}
          />
        )}
        <Footer
          showCart={showCart}
          totals={totals}
          findability={findability}
          checkout={checkout}
          onCartOpen={onCartOpen}
          schema={schema}
          onHomeClick={onHomeClick}
          renderError={renderError}
          showHint={() => {
            setNeedHint(true)
          }}
          closeSimulation={setClosed}
        />
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default NikeView
